import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/lib/auth";
import { avatarFor } from "@/data/demo";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

const TABS = ["ALL", "POSTS", "REPLIES", "SAVED", "LIKED", "READ", "SHARED", "COPILOT", "LEARNING", "HISTORY"];

const TYPE_META = {
  POST_CREATED: { icon: "PenSquare", color: "#2563eb", verb: "You published" },
  REPLY_CREATED: { icon: "MessageCircle", color: "#16a34a", verb: "You replied to" },
  LIKE_GIVEN: { icon: "ArrowBigUp", color: "#7c3aed", verb: "You upvoted" },
  CONTENT_VIEWED: { icon: "BookOpen", color: "#0891b2", verb: "You read" },
  COPILOT_QUERY: { icon: "Sparkles", color: "#d97706", verb: "You asked Copilot" },
  SAVE_CREATED: { icon: "Bookmark", color: "#db2777", verb: "You saved" },
  CONTENT_SHARED: { icon: "Send", color: "#0d9488", verb: "You shared" },
  EVENT_REGISTERED: { icon: "CalendarCheck", color: "#0891b2", verb: "You registered for" },
  LEARNING_COMPLETED: { icon: "GraduationCap", color: "#0891b2", verb: "You completed" },
};

const EMPTY_COPY = {
  ALL: { t: "Your MFDx journey starts here.", s: "Read something useful. Join a discussion. Ask Copilot. Save something you'll need later.", cta: "Explore MFDx", to: "/app" },
  POSTS: { t: "No posts yet.", s: "Share a win or a question — the community learns fastest from real MFDs.", cta: "Write a post", to: "/app/community" },
  REPLIES: { t: "No replies yet.", s: "Jump into a discussion and share your take.", cta: "Open community", to: "/app/community" },
  SAVED: { t: "Nothing saved yet.", s: "Save useful insights here. Your future self will thank you.", cta: "Browse intelligence", to: "/app/market" },
  LIKED: { t: "No upvotes yet.", s: "Upvote discussions you find valuable.", cta: "Open community", to: "/app/community" },
  READ: { t: "No reading history yet.", s: "Catch up on the latest MF industry reports.", cta: "Market Intelligence", to: "/app/market" },
  SHARED: { t: "Nothing shared yet.", s: "Turn one idea into posts for the community and beyond.", cta: "Open Publish", to: "/app/publish" },
  COPILOT: { t: "No Copilot chats yet.", s: "Ask Copilot how to grow SIP business or draft client content.", cta: "Ask Copilot", to: "/app/ai" },
  LEARNING: { t: "Your knowledge journey awaits.", s: "Watch a report or attend an investor awareness programme.", cta: "Start learning", to: "/app/market" },
  HISTORY: { t: "No activity yet.", s: "Everything you do on MFDx will show up here.", cta: "Explore MFDx", to: "/app" },
};

const fmtTs = (ts) => {
  if (!ts) return "";
  const d = new Date(ts), now = new Date();
  const days = Math.floor((now - d) / 86400000);
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  if (days === 0) return `Today · ${time}`;
  if (days === 1) return `Yesterday · ${time}`;
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const Stat = ({ label, value, icon, color }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 flex flex-col gap-1" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: `${color}14` }}><Icon name={icon} size={16} style={{ color }} /></span>
    </div>
    <div className="text-2xl font-head font-bold text-gray-900 mt-1">{(value ?? 0).toLocaleString("en-IN")}</div>
    <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</div>
  </div>
);

function Heatmap({ data, onPick }) {
  const map = Object.fromEntries((data || []).map((h) => [h.date, h.count]));
  const cells = [];
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 363);
  start.setDate(start.getDate() - start.getDay()); // align to Sunday
  const d = new Date(start);
  while (d <= end) {
    const iso = d.toISOString().slice(0, 10);
    cells.push({ date: iso, count: map[iso] || 0 });
    d.setDate(d.getDate() + 1);
  }
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const shade = (c) => (c === 0 ? "#f1f5f9" : c === 1 ? "#fde3b3" : c <= 3 ? "#fbbf24" : c <= 6 ? "#f59e0b" : "#d97706");
  return (
    <div className="overflow-x-auto pb-1" data-testid="activity-heatmap">
      <div className="flex gap-[3px]" style={{ minWidth: "max-content" }}>
        {weeks.map((w) => (
          <div key={w[0]?.date} className="flex flex-col gap-[3px]">
            {w.map((c) => (
              <button key={c.date} title={`${c.count} on ${c.date}`} onClick={() => onPick(c)}
                className="w-[10px] h-[10px] rounded-[2px] hover:ring-2 hover:ring-amber-300"
                style={{ background: shade(c.count) }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const GoalRow = ({ label, done, target }) => (
  <div className="mb-3 last:mb-0">
    <div className="flex justify-between text-xs mb-1"><span className="text-gray-600">{label}</span><span className="font-semibold text-gray-900">{done}/{target}</span></div>
    <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${target ? Math.min(100, (done / target) * 100) : 0}%` }} /></div>
  </div>
);

export default function MyMFDx() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [sum, setSum] = useState(null);
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [pickDay, setPickDay] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/activity/summary").then((r) => setSum(r.data)).catch(() => {});
    api.get("/activity/feed?tab=all&limit=150").then((r) => setAllItems(r.data.items)).catch(() => {});
  }, []);

  const loadFeed = useCallback((t, reset) => {
    const s = reset ? 0 : skip;
    setLoading(true);
    api.get(`/activity/feed?tab=${t.toLowerCase()}&skip=${s}&limit=15`).then((r) => {
      setItems((prev) => (reset ? r.data.items : [...prev, ...r.data.items]));
      setSkip(s + 15);
      setHasMore(r.data.has_more);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [skip]);

  useEffect(() => { loadFeed(tab, true); /* eslint-disable-next-line */ }, [tab]);

  const shown = q ? items.filter((i) => (i.title || "").toLowerCase().includes(q.toLowerCase())) : items;
  const dayItems = pickDay ? allItems.filter((i) => (i.ts || "").slice(0, 10) === pickDay.date) : [];

  const saveTargets = async (patch) => {
    const { data } = await api.post("/activity/goals", patch);
    setSum((s) => ({ ...s, goals: { ...s.goals, targets: data.goals } }));
    toast.success("Goal updated");
  };

  const removeSave = async (i) => {
    if (!i.save_id) return;
    await api.delete(`/activity/save/${i.save_id}`);
    setItems((p) => p.filter((x) => x.save_id !== i.save_id));
    toast.success("Removed from saved");
  };

  if (!sum) return <div className="p-8 text-center text-gray-400" data-testid="mymfdx-loading">Loading your MFDx…</div>;

  const p = sum.profile, st = sum.stats, jr = sum.journey, rc = sum.weekly_recap, g = sum.goals;
  const displayName = p.name || user?.name || "MFD";

  return (
    <div data-testid="mymfdx-page">
      <PageHeader title="My MFDx" subtitle="Your activity. Your knowledge. Your growth." icon="Compass" color="#f59e0b" />

      {/* Profile header */}
      <Card className="mb-5">
        <div className="flex items-start gap-4 flex-wrap">
          <img src={avatarFor(displayName)} alt={displayName} className="w-16 h-16 rounded-2xl border border-gray-100" data-testid="mymfdx-avatar" />
          <div className="flex-1 min-w-[180px]">
            <h2 className="text-xl font-head font-bold text-gray-900">{displayName}</h2>
            <p className="text-sm text-gray-500 capitalize">{p.role === "admin" ? "Admin" : "MFD"}{p.location ? ` · ${p.location}` : ""}{p.arn ? ` · ARN ${p.arn}` : ""}</p>
            {p.tagline && <p className="text-sm text-gray-600 mt-1 italic">“{p.tagline}”</p>}
          </div>
          <div className="w-full sm:w-64">
            <div className="flex justify-between text-xs mb-1"><span className="text-gray-500">MFDx Profile {p.completion}% complete</span></div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full grad-amber transition-all duration-700" style={{ width: `${p.completion}%` }} /></div>
            {p.completion < 100 && <button onClick={() => nav("/app/profile")} className="text-xs font-semibold text-amber-600 mt-2 hover:underline" data-testid="complete-profile-btn">Complete Profile →</button>}
          </div>
        </div>
      </Card>

      {sum.empty ? (
        <Card className="text-center py-16" data-testid="mymfdx-empty-hero">
          <div className="w-14 h-14 rounded-2xl grad-amber grid place-items-center mx-auto mb-4"><Icon name="Compass" size={26} className="text-black" /></div>
          <h3 className="text-lg font-head font-bold text-gray-900">Your MFDx journey starts here.</h3>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">Read something useful. Join a discussion. Ask Copilot. Save something you'll need later.</p>
          <Btn onClick={() => nav("/app")} className="mt-5" data-testid="empty-explore-btn"><Icon name="ArrowRight" size={16} /> Explore MFDx</Btn>
        </Card>
      ) : (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="mymfdx-stats">
            <Stat label="Days Active" value={st.days_active} icon="Flame" color="#f59e0b" />
            <Stat label="Posts" value={st.posts} icon="PenSquare" color="#2563eb" />
            <Stat label="Replies" value={st.replies} icon="MessageCircle" color="#16a34a" />
            <Stat label="Contributions" value={st.contributions} icon="Award" color="#7c3aed" />
            <Stat label="Content Read" value={st.content_read} icon="BookOpen" color="#0891b2" />
            <Stat label="Saved" value={st.saved} icon="Bookmark" color="#db2777" />
            <Stat label="Likes Received" value={st.likes_received} icon="ThumbsUp" color="#e11d48" />
            <Stat label="Followers" value={st.followers} icon="Users" color="#0d9488" />
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {/* LEFT: streak + journey + goals */}
            <div className="space-y-5">
              <Card data-testid="mymfdx-streak">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Flame" size={18} className="text-amber-500" />
                  <div><div className="font-head font-bold text-gray-900">{sum.streak} day streak</div><div className="text-xs text-gray-400">Keep your learning streak going.</div></div>
                </div>
                <Heatmap data={sum.heatmap} onPick={setPickDay} />
                {pickDay && (
                  <div className="mt-3 rounded-xl bg-gray-50 p-3" data-testid="heatmap-day-detail">
                    <div className="text-xs font-semibold text-gray-700">{pickDay.count} {pickDay.count === 1 ? "activity" : "activities"} on {new Date(pickDay.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>
                    {dayItems.slice(0, 4).map((i, x) => <div key={`${i.type}-${i.ts || x}`} className="text-xs text-gray-500 mt-1">· {(TYPE_META[i.type]?.verb || "Activity")} {i.title?.slice(0, 40)}</div>)}
                    {dayItems.length === 0 && <div className="text-xs text-gray-400 mt-1">No detailed activity recorded.</div>}
                  </div>
                )}
              </Card>

              <Card data-testid="mymfdx-journey">
                <h3 className="font-head font-bold text-gray-900 mb-1">Your MFDx Journey</h3>
                <p className="text-xs text-gray-400 mb-4">Milestones based on real, useful actions.</p>
                <div className="space-y-2">
                  {jr.stages.map((s, i) => (
                    <div key={s} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${i === jr.index ? "bg-amber-50 border border-amber-200" : ""}`}>
                      <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${i < jr.index ? "bg-emerald-500 text-white" : i === jr.index ? "grad-amber text-black" : "bg-gray-100 text-gray-400"}`}>{i < jr.index ? "✓" : i + 1}</span>
                      <span className={`text-sm ${i === jr.index ? "font-bold text-gray-900" : i < jr.index ? "text-gray-500" : "text-gray-400"}`}>{s}</span>
                      {i === jr.index && <span className="ml-auto text-[10px] font-bold bg-amber-500 text-black rounded-full px-2 py-0.5">YOU</span>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-3"><Icon name="Target" size={12} className="inline text-amber-500" /> {jr.next_hint}</p>
              </Card>

              <Card data-testid="mymfdx-goals">
                <h3 className="font-head font-bold text-gray-900 mb-1">My MFDx Goal <span className="text-xs font-normal text-gray-400">(this week)</span></h3>
                <p className="text-xs text-gray-400 mb-4">You're already partway there — keep going.</p>
                <GoalRow label="Read intelligence updates" done={g.progress.read} target={g.targets.read} />
                <GoalRow label="Join discussions" done={g.progress.discuss} target={g.targets.discuss} />
                <GoalRow label="Learn a new topic" done={g.progress.learn} target={g.targets.learn} />
                <div className="flex gap-2 mt-3">
                  <button onClick={() => saveTargets({ read: g.targets.read + 1 })} className="text-xs text-gray-500 hover:text-amber-600" data-testid="goal-read-up">+ Read goal</button>
                  <button onClick={() => saveTargets({ discuss: g.targets.discuss + 1 })} className="text-xs text-gray-500 hover:text-amber-600" data-testid="goal-discuss-up">+ Discuss goal</button>
                </div>
              </Card>
            </div>

            {/* CENTER: tabs + feed */}
            <div className="lg:col-span-1">
              <Card className="!p-0 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <div className="relative">
                    <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your MFDx activity…" data-testid="activity-search" className="w-full rounded-xl border border-gray-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                  </div>
                </div>
                <div className="flex gap-1 px-3 py-2 overflow-x-auto border-b border-gray-100" data-testid="activity-tabs">
                  {TABS.map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${tab === t ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"}`}>{t}</button>
                  ))}
                </div>
                <div className="p-4">
                  {shown.length === 0 && !loading ? (
                    <div className="text-center py-10" data-testid="tab-empty">
                      <p className="font-semibold text-gray-800">{EMPTY_COPY[tab].t}</p>
                      <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">{EMPTY_COPY[tab].s}</p>
                      <Btn onClick={() => nav(EMPTY_COPY[tab].to)} className="mt-4" data-testid="tab-empty-cta">{EMPTY_COPY[tab].cta}</Btn>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {shown.map((i, x) => {
                        const m = TYPE_META[i.type] || { icon: "Circle", color: "#94a3b8", verb: "Activity" };
                        return (
                          <div key={`${i.type}-${i.ts}-${x}`} className="flex gap-3 rounded-xl p-3 hover:bg-gray-50 cursor-pointer group" onClick={() => i.link && i.link !== "#" && nav(i.link)} data-testid="activity-item">
                            <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${m.color}14` }}><Icon name={m.icon} size={15} style={{ color: m.color }} /></span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900"><span className="text-gray-500">{m.verb} </span><span className="font-semibold">{i.title}</span></div>
                              {i.snippet && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{i.snippet}</div>}
                              <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                                <span>{fmtTs(i.ts)}</span>
                                {i.category && <span className="text-violet-500">{i.category}</span>}
                                {typeof i.likes === "number" && i.type === "POST_CREATED" && <span>· {i.likes} likes · {i.replies} replies</span>}
                              </div>
                            </div>
                            {i.type === "SAVE_CREATED" && <button onClick={(e) => { e.stopPropagation(); removeSave(i); }} className="text-gray-300 hover:text-red-500 shrink-0" data-testid="unsave-btn"><Icon name="X" size={15} /></button>}
                          </div>
                        );
                      })}
                      {hasMore && <button onClick={() => loadFeed(tab, false)} disabled={loading} className="w-full text-sm font-semibold text-amber-600 py-2 hover:underline" data-testid="load-more">{loading ? "Loading…" : "Load more"}</button>}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* RIGHT: insights + weekly recap + recognition */}
            <div className="space-y-5">
              <Card data-testid="mymfdx-recap">
                <h3 className="font-head font-bold text-gray-900 mb-3">Your Week on MFDx</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[["Activities", rc.activities], ["Discussions", rc.discussions], ["Read", rc.read], ["Saved", rc.saved], ["Copilot", rc.copilot]].map(([l, v]) => (
                    <div key={l}><div className="text-xl font-head font-bold text-gray-900">{v}</div><div className="text-[10px] text-gray-400 uppercase">{l}</div></div>
                  ))}
                </div>
                {rc.focus && <p className="text-xs text-gray-500 mt-3">Your focus this week: <span className="font-semibold text-gray-800">{rc.focus}</span></p>}
              </Card>

              {sum.insights.length > 0 && (
                <Card data-testid="mymfdx-insights">
                  <h3 className="font-head font-bold text-gray-900 mb-3 flex items-center gap-2"><Icon name="Lightbulb" size={16} className="text-amber-500" /> Your MFDx Insights</h3>
                  <ul className="space-y-2">
                    {sum.insights.map((ins, x) => <li key={`${x}-${ins.slice(0, 24)}`} className="text-sm text-gray-600 flex gap-2"><Icon name="TrendingUp" size={14} className="text-amber-500 shrink-0 mt-0.5" />{ins}</li>)}
                  </ul>
                </Card>
              )}

              {sum.recognition && (
                <Card className="!bg-emerald-50 border border-emerald-100" data-testid="mymfdx-recognition">
                  <div className="flex gap-3"><Icon name="Sparkles" size={18} className="text-emerald-600 shrink-0" /><p className="text-sm text-emerald-900 font-medium">{sum.recognition}</p></div>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
